/**
 * PATH: packages/ui/src/kanban/components/EmailThreadReplyBar.tsx
 * FILE_ID: UI-KANBAN-EMAIL-REPLYBAR-001
 * PURPOSE: Footer action bar of the card-drawer email reader (Reply /
 *          Reply-all / Forward / Open-in-hub) PLUS the expand-in-place
 *          inline composer those actions open. Extracted from
 *          EmailThreadView.tsx (operator 500-line ceiling).
 * WHY:     ARC-1 (FEAT-2026-07-02-KANBAN-REPLY-IN-CARD). Client: "when
 *          replying etc. it redirects to the email window when in drawer
 *          … this fucks things up." When the tenant adapter injects
 *          `InlineComposer` (the Email Hub's canonical composer via
 *          kanban-host), the reply buttons expand a full compose form
 *          below the thread — the card stays open, nothing navigates.
 *          Read-only bundles without a composer keep the old
 *          `buildComposeUrl` deep-link anchors as a fallback.
 * FLOW:
 *   ┌────────────┐   ┌──────────────────┐   ┌───────────────────────┐
 *   │ Reply click│ → │ composeMode set  │ → │ adapter.InlineComposer│
 *   │ (button)   │   │ (reply/all/fwd)  │   │ mounted in place      │
 *   └────────────┘   └──────────────────┘   └───────────────────────┘
 * RELATED:
 *   - EmailThreadView.tsx: renders this under the message body.
 *   - api/adapters-email.ts: KanbanInlineComposerProps contract.
 *   - @quotech/kanban-host emailAdapter.ts: injects the hub composer.
 */
import { useState } from 'react'
import {
  ArrowUpRight,
  Forward,
  Paperclip,
  PenSquare,
  Reply,
  ReplyAll,
  X,
} from 'lucide-react'

import type { KanbanEmailAdapter } from '../api'

export type InlineComposeMode = 'reply' | 'reply_all' | 'forward' | 'new'

const MODE_TITLES: Record<InlineComposeMode, string> = {
  reply: 'Reply',
  reply_all: 'Reply all',
  forward: 'Forward',
  new: 'New email',
}

export interface EmailThreadReplyBarProps {
  /** The email the actions are seeded off (the currently-open message). */
  emailId: string
  adapter: KanbanEmailAdapter
  /** Attachment count of the open message — rendered in the footer. */
  attachmentCount?: number
  /** The card the drawer belongs to. Threaded into the inline composer
   *  so the sent copy links back to the card (`linked_card_ids`). */
  cardId?: string | null
  /** Called after a successful inline send — the thread view refetches
   *  so the sent message appears in the chain without leaving the card. */
  onSent?: () => void
}

export function EmailThreadReplyBar({
  emailId,
  adapter,
  attachmentCount,
  cardId,
  onSent,
}: EmailThreadReplyBarProps) {
  const [composeMode, setComposeMode] = useState<InlineComposeMode | null>(null)

  const InlineComposer = adapter.InlineComposer
  const inline = Boolean(InlineComposer)

  const actionBtnClass = (active: boolean, primary: boolean) =>
    [
      'inline-flex items-center gap-1 rounded px-2 py-1 text-[11px]',
      primary
        ? 'bg-blue-600 px-2.5 font-semibold text-white hover:bg-blue-700'
        : 'bg-neutral-100 font-medium text-neutral-700 hover:bg-neutral-200 dark:bg-zinc-800 dark:text-zinc-200 dark:hover:bg-zinc-700',
      active ? 'ring-2 ring-blue-400 dark:ring-blue-500' : '',
    ].join(' ')

  return (
    <>
      {/* Expand-in-place composer. Keyed on mode+email so switching
          Reply → Forward remounts a fresh seed instead of mutating the
          open draft's intent. */}
      {inline && InlineComposer && composeMode && (
        <div
          data-testid="email-thread-inline-composer"
          className="flex h-[440px] shrink-0 flex-col border-t border-neutral-200 bg-white dark:border-zinc-800 dark:bg-zinc-900"
        >
          <div className="flex items-center justify-between border-b border-neutral-100 px-3 py-1.5 dark:border-zinc-800">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-neutral-500 dark:text-zinc-400">
              {MODE_TITLES[composeMode]}
            </span>
            <button
              type="button"
              onClick={() => setComposeMode(null)}
              aria-label="Close composer"
              className="rounded p-1 text-neutral-500 hover:bg-neutral-100 dark:text-zinc-400 dark:hover:bg-zinc-800"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto">
            <InlineComposer
              key={`${composeMode}:${emailId}`}
              mode={composeMode}
              emailId={composeMode === 'new' ? null : emailId}
              cardId={cardId ?? null}
              onSent={() => {
                setComposeMode(null)
                onSent?.()
              }}
              onClose={() => setComposeMode(null)}
            />
          </div>
        </div>
      )}

      {/* Footer — attachment count + Reply actions + Email Hub
          deep-link. FEAT-2026-06-07-KANBAN-EMAIL-REPLY introduced the
          actions; ARC-1 upgraded them from compose-route deep-links to
          the in-place composer above (deep-links remain the fallback
          when the adapter ships no composer). */}
      <footer className="flex items-center justify-between gap-2 border-t border-neutral-200 px-3 py-1.5 dark:border-zinc-800">
        <div className="flex min-w-0 items-center gap-3 text-[11px] text-neutral-500">
          {attachmentCount ? (
            <span className="inline-flex items-center gap-1">
              <Paperclip className="h-3 w-3" />
              {attachmentCount} attachment
              {attachmentCount === 1 ? '' : 's'}
            </span>
          ) : null}
        </div>
        <div className="flex flex-shrink-0 items-center gap-1.5">
          {inline ? (
            <>
              <button
                type="button"
                onClick={() =>
                  setComposeMode((m) => (m === 'reply' ? null : 'reply'))
                }
                className={actionBtnClass(composeMode === 'reply', true)}
                data-testid="email-thread-reply"
              >
                <Reply className="h-3 w-3" /> Reply
              </button>
              <button
                type="button"
                onClick={() =>
                  setComposeMode((m) => (m === 'reply_all' ? null : 'reply_all'))
                }
                className={actionBtnClass(composeMode === 'reply_all', false)}
                data-testid="email-thread-reply-all"
                title="Reply to sender + all original recipients"
              >
                <ReplyAll className="h-3 w-3" /> Reply all
              </button>
              <button
                type="button"
                onClick={() =>
                  setComposeMode((m) => (m === 'forward' ? null : 'forward'))
                }
                className={actionBtnClass(composeMode === 'forward', false)}
                data-testid="email-thread-forward"
                title="Forward this message"
              >
                <Forward className="h-3 w-3" /> Forward
              </button>
              {/* ARC-1 MR2 — send a brand-new email FROM the card (not a
                  reply). The sent copy links back to the card via
                  `linked_card_ids` so it shows under related emails. */}
              <button
                type="button"
                onClick={() =>
                  setComposeMode((m) => (m === 'new' ? null : 'new'))
                }
                className={actionBtnClass(composeMode === 'new', false)}
                data-testid="email-thread-compose-new"
                title="Send a new email from this card"
              >
                <PenSquare className="h-3 w-3" /> New email
              </button>
            </>
          ) : adapter.buildComposeUrl ? (
            <>
              <a
                href={adapter.buildComposeUrl(emailId, 'reply')}
                className={actionBtnClass(false, true)}
                data-testid="email-thread-reply"
              >
                <Reply className="h-3 w-3" /> Reply
              </a>
              <a
                href={adapter.buildComposeUrl(emailId, 'reply_all')}
                className={actionBtnClass(false, false)}
                data-testid="email-thread-reply-all"
                title="Reply to sender + all original recipients"
              >
                <ReplyAll className="h-3 w-3" /> Reply all
              </a>
              <a
                href={adapter.buildComposeUrl(emailId, 'forward')}
                className={actionBtnClass(false, false)}
                data-testid="email-thread-forward"
                title="Forward this message"
              >
                <Forward className="h-3 w-3" /> Forward
              </a>
            </>
          ) : null}
          {adapter.buildEmailHubUrl && (
            <a
              href={adapter.buildEmailHubUrl(emailId)}
              className={actionBtnClass(false, false)}
            >
              Open in Email Hub <ArrowUpRight className="h-3 w-3" />
            </a>
          )}
        </div>
      </footer>
    </>
  )
}
